import React, { useContext, useEffect, useLayoutEffect, useRef } from "react";
import { VariantContextData } from "../variants/VariantBuilderContext";
import StepBuilder from "../steps/StepBuilder";
import useSteps from "../steps/useSteps.js";
import useSections from "../sections/useSections.js";
import useSectionColumns from "../section_columns/useSectionColumns";
import useElements from "../elements/useElements.js";
import useFields from "../fields/useFields.js";
import useContentToggle from "../content_toggles/useContentToggle";
import useReviews from "../reviews/useReviews";
import VariantClose from "../variants/VariantClose";
import ZoomableCanvas from "../shared/ZoomableCanvas";
import VariantBuilderCanvasRoundedCorner from "../variants/VariantBuilderCanvasRoundedCorner";
import VariantBuilderCanvasSelect from "./VariantBuilderCanvasSelect.js";
import VariantBuilderCanvasSelectPopover from "./VariantBuilderCanvasSelectPopover.js";
import { attachElementCanvasDelegation } from "../../utils/elementCanvasDelegation";
import { attachObjectCanvasDelegation } from "../../utils/objectCanvasDelegation";
import apiRequest from "../../utils/apiRequest";
import { createConsumer } from "@rails/actioncable";
import { buildDuplicateSelection } from "./duplicateSelection";
import { hasPendingDuplicate } from "../../utils/builderUtils";
import {
  observeMergeTagChips,
  removeChipFromHtml,
} from "../../utils/mergeTagChips";
import { buildMergeTagGroups } from "../../utils/mergeTagOptions";
import VariantTwoTapPopup from "../variants/VariantTwoTapPopup";

const VariantBuilderCanvas = (props) => {
  const [contextData, builder] = useContext(VariantContextData);
  const { Steps } = useSteps();
  const { Sections } = useSections();
  const { SectionColumns } = useSectionColumns();
  const { Elements } = useElements(Sections);
  const { Fields } = useFields();
  const { ContentToggle } = useContentToggle(Sections);
  const { Reviews } = useReviews();

  let website = contextData.objects.website;
  let cta = contextData.objects.cta;
  let variant = contextData.objects.variant;
  let user = contextData.objects.users.filter((user) => {
    return user.id == variant.user_id;
  })[0];
  const sorted_steps = Object.values(variant.steps)
    .filter((s) => s.toBeDeleted !== true)
    .sort((a, b) => a.position - b.position);

  let cta_type_type = cta[cta.cta_type] ? cta[cta.cta_type] : "";

  const selectPromptSettings = (event) => {
    event.preventDefault();
    event.stopPropagation();
    builder.select("variants", variant.id, null, "Popup Trigger", false, false);
  };

  const triggerType = variant.options["trigger_type"];
  const isPopupTrigger = ["prompt", "two-tap", "prehook"].includes(triggerType);
  const popupTitle =
    triggerType === "prompt"
      ? "Messenger Popup"
      : triggerType === "two-tap"
      ? "Two-tap Floating Button"
      : "On-site Message";
  const popupClass =
    triggerType === "prehook"
      ? "cf-prehook-popup"
      : triggerType === "prompt"
      ? "cf-prompt-popup"
      : "cf-two-tap";

  function objectToUse(object_type, object_id) {
    return contextData.objects.variant[object_type][object_id];
  }

  function hookToUse(object_type) {
    let hookToUse;
    if (object_type == "steps") {
      hookToUse = Steps;
    }
    if (object_type == "sections") {
      hookToUse = Sections;
    }
    if (object_type == "section_columns") {
      hookToUse = SectionColumns;
    }
    if (object_type == "elements") {
      hookToUse = Elements;
    }
    if (object_type == "fields") {
      hookToUse = Fields;
    }
    if (object_type == "content_toggle_items") {
      hookToUse = ContentToggle;
    }
    if (object_type == "reviews") {
      hookToUse = Reviews;
    }
    return hookToUse;
  }

  const applyEditorSelect = (
    object_type,
    object_id,
    selector,
    attempt = 1,
    maxAttempts = 12,
    delayMs = 120
  ) => {
    if (
      window.selected_object_type !== object_type ||
      window.selected_object_id !== object_id
    ) {
      return;
    }

    const $matches = $(selector);
    if ($matches.length) {
      $matches.addClass("editor-select");
      return;
    }

    if (attempt < maxAttempts) {
      setTimeout(
        () => applyEditorSelect(object_type, object_id, selector, attempt + 1, maxAttempts, delayMs),
        delayMs
      );
    }
  };

  const handledSelect = function (event) {
    const details = event.detail;
    builder.select(
      details.object_type,
      details.object_id,
      details.tab,
      details.collapse,
      details.editing,
      details.panel_closed
    );

    if (details.editing == true) {
      const object = objectToUse(details.object_type, details.object_id);
      const hook = hookToUse(details.object_type);
      hook.froalaEditor(website, object, details.target);
    }

    if (details.object_type == "elements") {
      applyEditorSelect(details.object_type, details.object_id, `.cf-element[data-object-type="elements"][data-element-id="${details.object_id}"]`);
    }

    if (details.object_type == "sections") {
      applyEditorSelect(details.object_type, details.object_id, `.cf-section[data-object-type="sections"][data-section-id="${details.object_id}"], .cf-section-overlay[data-section-id="${details.object_id}"]`);
    }

    if (details.object_type == "steps") {
      applyEditorSelect(details.object_type, details.object_id, `.canvas[data-step-id="${details.object_id}"]`);
    }
  };

  const runAsyncBuilderJob = async function ({
    endpoint,
    body,
    object_type,
    tempObjectId,
    onRemove,
    onReady,
  }) {
    const removePlaceholder = onRemove
      ? onRemove
      : () => builder.removeReplicaObjectFromBuilder(object_type, tempObjectId);

    const createChannelSubscription = (cableSyncId) => {
      return new Promise((resolve, reject) => {
        const cable = createConsumer();

        const JOB_TIMEOUT = 100000;
        let timeoutId = null;
        let isResolved = false;

        const subscription = cable.subscriptions.create(
          { channel: "BuilderDuplicationChannel", id: cableSyncId },
          {
            connected: () => {
              if (!isResolved) {
                isResolved = true;
                resolve({ subscription, cable });
              }
            },
            received: (data) => {
              const teardown = () => {
                if (timeoutId) clearTimeout(timeoutId);
                subscription.unsubscribe();
                cable.disconnect();
              };

              switch (data['status']) {
                case "ready":
                  teardown();
                  onReady(data);
                  break;
                case "failed":
                  teardown();
                  console.error('Builder job failed on server:', data.error);
                  removePlaceholder();
                  break;
                default:
                  console.log(`Builder job status: ${data['status']} (still in progress)`);
              }
            },
          }
        );

        timeoutId = setTimeout(() => {
          console.warn('Builder job timed out, disconnecting subscription');
          subscription.unsubscribe();
          cable.disconnect();
          removePlaceholder();

          if (!isResolved) {
            isResolved = true;
            reject(new Error('Subscription timeout'));
          }
        }, JOB_TIMEOUT);
      });
    };

    try {
      const cableSyncId = crypto.randomUUID();
      await createChannelSubscription(cableSyncId);

      apiRequest("POST", endpoint, {
        cable_sync_id: cableSyncId,
        ...body,
      });
    } catch (error) {
      console.error('Error during async builder job:', error);
      removePlaceholder();
    }
  };

  const handleDuplicate = async function (event) {
    const details = event.detail;

    let target_object_type = details.object_type;
    let target_object_id = details.object_id;

    const handleSuccess = (data) => {
      let updates = [];
      const cancelledReplicaKey = `${target_object_type}-${target_object_id}`;
      const duplicateWasCancelled =
        window.cancelledDuplicateReplicas &&
        window.cancelledDuplicateReplicas[cancelledReplicaKey] == true;

      if (target_object_type == "steps") {
        updates = Steps.reorderStepPositions(data.object.position);
      }

      builder.removeReplicaObjectFromBuilder(
        target_object_type,
        target_object_id
      );

      if (duplicateWasCancelled) {
        delete window.cancelledDuplicateReplicas[cancelledReplicaKey];
        data.object.toBeDeleted = true;
        builder.addBuilderObjectFromApiToBuilder(
          target_object_type,
          data.object,
          false,
          updates,
          false
        );
        dispatchCustomEvent("navigateTab", { view: "layers" });
        return;
      }

      builder.addBuilderObjectFromApiToBuilder(
        target_object_type,
        data.object,
        false,
        updates,
        false
      );

      const selection =
        details.duplicate_params?.upgrade_in_place &&
        details.duplicate_params?.section_id
          ? {
              object_type: "sections",
              object_id: details.duplicate_params.section_id,
              tab: "Section",
            }
          : buildDuplicateSelection(target_object_type, data.object);

      if (target_object_type !== "section_columns") {
        dispatchCustomEvent("selectObject", selection);
      }

      builder.scrollToObject(selection.object_type, selection.object_id);
    };

    const upgradeInPlace = details.duplicate_params?.upgrade_in_place;
    const scrollReplicaIntoView = target_object_type == "steps";
    if (!upgradeInPlace) {
      if (
        hasPendingDuplicate(
          contextData.objects.variant,
          target_object_type,
          target_object_id
        )
      ) {
        return;
      }

      builder.addReplicaObjectToBuilder(target_object_type, target_object_id, scrollReplicaIntoView);
    }

    runAsyncBuilderJob({
      endpoint: `/websites/${website.id}/builder/duplicate`,
      object_type: target_object_type,
      tempObjectId: target_object_id,
      onReady: handleSuccess,
      body: {
        object_type: target_object_type,
        object_id: target_object_id,
        duplicate_params: {
          website_id: website.id,
          variant_id: variant.id,
          builder_changes: JSON.stringify(builder.builderChangesToAsyncDuplicate()),
          ...(details.duplicate_params || {})
        }
      },
    });
  };

  const handleCreateFromTemplate = async function (event) {
    const details = event.detail;

    const object_type = details.object_type;
    const template_id = details.template_id;
    const parent = details.parent || {};
    const element_type = details.element_type;
    const onReadyCallback = details.onReady;

    const tempObjectId = builder.addTemplatePlaceholderToBuilder(object_type, {
      ...parent,
      element_type,
    });

    const removePlaceholder = () =>
      builder.removeTemplatePlaceholderFromBuilder(object_type, tempObjectId);

    const handleSuccess = (data) => {
      let updates = [];
      if (object_type == "steps") { updates = Steps.reorderStepPositions(data.object.position) }

      removePlaceholder();
      builder.addBuilderObjectFromApiToBuilder(object_type, data.object, false, updates, false);

      if (onReadyCallback) {
        onReadyCallback(data.object);
      }

      const selection = buildDuplicateSelection(object_type, data.object);
      dispatchCustomEvent("selectObject", selection);
      builder.scrollToObject(selection.object_type, selection.object_id);
    };

    runAsyncBuilderJob({
      endpoint: `/websites/${website.id}/builder/create_from_template`,
      object_type: object_type,
      tempObjectId: tempObjectId,
      onRemove: removePlaceholder,
      onReady: handleSuccess,
      body: {
        object_type: object_type,
        create_params: {
          template_id: template_id,
          cta_id: cta.id,
          variant_id: variant.id,
          step_id: parent.step_id,
          section_id: parent.section_id,
          position: parent.position,
          column: parent.column,
          element_type: element_type,
          element_params:
            object_type == "elements"
              ? { column: parent.column, position: parent.position }
              : undefined,
        },
      },
    });
  };

  const handleMoveUp = function (event) {
    const details = event.detail;
    const object = objectToUse(details.object_type, details.object_id);
    const hook = hookToUse(details.object_type);
    hook.moveUp(object);
  };

  const handleMoveDown = function (event) {
    const details = event.detail;
    const object = objectToUse(details.object_type, details.object_id);
    const hook = hookToUse(details.object_type);
    hook.moveDown(object);
  };

  const handleRemove = function (event) {
    const details = event.detail;
    const object = objectToUse(details.object_type, details.object_id);
    const hook = hookToUse(details.object_type);
    hook.remove(object);
  };

  const handleUpdate = function (event) {
    const updates = event.detail.updates;
    if (updates) {
      builder.update(updates);
    }
  };

  const handleNewObject = function (event) {
    builder.new_object(event.detail);
  };

  const handleNavigation = function (event) {
    const details = event.detail;
    builder.view(details.view, details.collapse);
  };

  const handleFocus = function (event) {
    const details = event.detail;
    builder.focus(
      details.parent_id,
      details.item_type,
      details.item_id,
      details.triggerPanelVisibility
    );
  };

  const resetStyles = function (event) {
    const details = event.detail;
    const object = objectToUse(details.object_type, details.object_id);
    const hook = hookToUse(details.object_type);
    hook.reset(object);
  };

  const liveRef = useRef(null);
  useLayoutEffect(() => {
    liveRef.current = {
      contextData,
      handledSelect,
      handleDuplicate,
      handleCreateFromTemplate,
      handleMoveUp,
      handleMoveDown,
      handleRemove,
      handleUpdate,
      handleNewObject,
      handleNavigation,
      handleFocus,
      resetStyles,
    };
  });

  useEffect(() => {
    if (props.page !== "builder") return;

    const canvas = document.querySelector("#builder");
    if (!canvas) return;

    canvas.onmouseover = function () {
      if (canvas.getAttribute("data-engaged") !== "true") {
        canvas.setAttribute("data-engaged", "true");
      }
    };
    canvas.onmouseleave = function () {
      if (canvas.getAttribute("data-engaged") == "true") {
        canvas.setAttribute("data-engaged", "false");
      }
    };

    const detachElementDelegation = attachElementCanvasDelegation(
      canvas,
      (id) => liveRef.current.contextData.objects.variant.elements[id],
      () => ({
        object_type: window.selected_object_type,
        object_id: window.selected_object_id,
        editing: window.selected_object_editing,
      })
    );

    const detachObjectDelegation = attachObjectCanvasDelegation(
      canvas,
      (id) => {
        const variant = liveRef.current.contextData.objects.variant;
        const section = variant.sections[id];
        return (
          section && { ...section, element: variant.elements[section.element_id] }
        );
      },
      (id) => liveRef.current.contextData.objects.variant.steps[id],
      (id) =>
        (liveRef.current.contextData.objects.variant.section_columns || {})[id],
      (id) => liveRef.current.contextData.objects.variant.elements[id]
    );

    const detachMergeTagChips = observeMergeTagChips(canvas, () => {
      const { website, variant } = liveRef.current.contextData.objects;
      return buildMergeTagGroups({
        website,
        fields: variant.fields,
        actions: variant.actions,
      });
    });

    const onChipRemove = (e) => {
      const chip = e.target.closest?.(".cf-merge-chip-remove")?.parentElement;
      if (!chip || chip.closest(".fr-element")) return;
      const elementId = chip
        .closest(".cf-element")
        ?.getAttribute("data-element-id");
      const element =
        liveRef.current.contextData.objects.variant.elements[elementId];
      const root = chip.closest(
        `#element${elementId}-content > .froala-editor`
      );
      if (
        !root ||
        !element ||
        element.toBeDeleted ||
        !["headline", "text"].includes(element.element_type)
      ) {
        return;
      }
      const html = removeChipFromHtml(element.html, root, chip);
      if (html === element.html) return;

      e.preventDefault();
      e.stopPropagation();
      liveRef.current.handleUpdate({
        detail: {
          updates: [
            {
              object_type: "elements",
              object_id: element.id,
              setting_name: "[html]",
              value: html,
            },
          ],
        },
      });
    };
    canvas.addEventListener("click", onChipRemove, true);

    const onSelect = (e) => liveRef.current.handledSelect(e);
    const onDuplicate = (e) => liveRef.current.handleDuplicate(e);
    const onCreateFromTemplate = (e) =>
      liveRef.current.handleCreateFromTemplate(e);
    const onMoveUp = (e) => liveRef.current.handleMoveUp(e);
    const onMoveDown = (e) => liveRef.current.handleMoveDown(e);
    const onRemove = (e) => liveRef.current.handleRemove(e);
    const onUpdate = (e) => liveRef.current.handleUpdate(e);
    const onNewObject = (e) => liveRef.current.handleNewObject(e);
    const onPanelView = (e) => liveRef.current.handleNavigation(e);
    const onFocusItem = (e) => liveRef.current.handleFocus(e);
    const onResetStyles = (e) => liveRef.current.resetStyles(e);

    document.addEventListener("selectObject", onSelect);
    document.addEventListener("duplicateObject", onDuplicate);
    document.addEventListener("createObjectFromTemplate", onCreateFromTemplate);
    document.addEventListener("moveObjectUp", onMoveUp);
    document.addEventListener("moveObjectDown", onMoveDown);
    document.addEventListener("removeObject", onRemove);
    document.addEventListener("updateBuilder", onUpdate);
    document.addEventListener("newObject", onNewObject);
    document.addEventListener("panelView", onPanelView);
    document.addEventListener("focusItem", onFocusItem);
    document.addEventListener("resetStyles", onResetStyles);

    return function cleanup() {
      detachElementDelegation();
      detachObjectDelegation();
      detachMergeTagChips();
      canvas.removeEventListener("click", onChipRemove, true);
      document.removeEventListener("selectObject", onSelect);
      document.removeEventListener("duplicateObject", onDuplicate);
      document.removeEventListener(
        "createObjectFromTemplate",
        onCreateFromTemplate
      );
      document.removeEventListener("moveObjectUp", onMoveUp);
      document.removeEventListener("moveObjectDown", onMoveDown);
      document.removeEventListener("removeObject", onRemove);
      document.removeEventListener("updateBuilder", onUpdate);
      document.removeEventListener("newObject", onNewObject);
      document.removeEventListener("panelView", onPanelView);
      document.removeEventListener("focusItem", onFocusItem);
      document.removeEventListener("resetStyles", onResetStyles);
    };
  }, [props.page]);

  useEffect(() => {
    if (props.page == "builder") {
      Steps.sortableStart();
    }
  });

  useEffect(() => {
    if (window.history && window.history.pushState && environment !== "test") {
      window.onpopstate = (event) => {
        // Possible scenarios:
        // when enters page, does nothing and click on browser back/forward button
        // when enters page, make changes and click on browser back/forward button
        // when enters page after clicking on browser back/forward button on another CTA, make changes and click on browser back/forward button
        if (event.state == "contextDataChanged") {
          if (
            contextData.history.unsaved == true ||
            contextData.history.unpublished == true
          ) {
            let confirmation =
              window.cfBackButtonPressed == true
                ? true
                : confirm(
                    "You have unsaved changes, are you sure want to leave?"
                  );

            if (
              confirmation == true &&
              window.cfReachedDifferentPath !== true
            ) {
              window.cfBackButtonPressed = true;
              history.back();
            }
          } else {
            history.back();
          }
        } else {
          let isPreviousTurbolinks = Object.keys(event.state).includes(
            "turbolinks"
          );
          let isPreviousPathEqualCurrent =
            document.location.pathname == window.cfLastLocationPath;

          if (!isPreviousPathEqualCurrent) {
            window.cfReachedDifferentPath = true;
          }

          if (
            (isPreviousTurbolinks && isPreviousPathEqualCurrent) ||
            (!isPreviousPathEqualCurrent &&
              window.cfReachedDifferentPath !== true)
          ) {
            history.back();
          }
        }
      };
    }
  }, []);

  useEffect(() => {
    // popstate just works with history.pushState
    window.cfLastLocationPath = location.pathname + location.search;
    window.cfBackButtonPressed = false;
    window.cfReachedDifferentPath = false;
    window.history.pushState("contextDataChanged", null, null);
    builder.unsavedWarning();

    window.onkeydown = function (e) {
      if (contextData.selected_object_editing !== true) {
        if (e.metaKey && e.keyCode == 83) {
          if (contextData.history.unsaved == true) {
            builder.save();
          }
          return false;
        }

        if (e.metaKey && e.shiftKey == false && e.keyCode == 90) {
          builder.undo();
        }

        if (e.metaKey && e.shiftKey == true && e.keyCode == 90) {
          builder.redo();
        }

        if (e.keyCode == 8) {
          let canvas = document.querySelector("#builder");
          const object_type = builder.selected().object_type;

          if (object_type && canvas.getAttribute("data-engaged") == "true") {
            hookToUse(object_type).remove(builder.selected().object);
          }
        }
      }
    };
  }, [contextData]);

  const canvasContainer = () => {
    const containerBackground =
      contextData.device == "mobile" &&
      variant.options["container-background-mobile"]
        ? variant.options["container-background-mobile"]
        : variant.options["container-background"];
    const containerBackgroundColor =
      contextData.device == "mobile" &&
      variant.options["container-background-color-mobile"]
        ? variant.options["container-background-color-mobile"]
        : variant.options["container-background-color"];

    return (
      <div className="h-screen flex-grow w-full">
        <div
          id="builder"
          cta-id={cta.id}
          cta-type={cta.cta_type}
          className={`variant-edit-canvas ${cta.cta_type} ${cta_type_type}`}
          style={{
            background:
              containerBackground == "custom"
                ? containerBackgroundColor
                : undefined,
          }}
        >
          <div
            id="canvas-container"
            className={"row hard-center canvas-container-" + contextData.device}
            data-editing={contextData.selected_object_editing}
          >
            <div
              id={"cta_" + cta.id}
              className={
                "steps-container cf-" + cta.cta_type + "-" + cta_type_type
              }
              data-cta-id={cta.id}
              data-variant-id={variant.id}
              data-variant-variation={variant.variation}
            >
              {isPopupTrigger && (
                <div
                  className="row some-vertical-space"
                  onClick={selectPromptSettings}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      selectPromptSettings(event);
                    }
                  }}
                >
                  <div className="row hard-center step-toggle">
                    <h4 className={`step-headline text-xl font-semibold ${cta.cta_type == "overlay" && variant.options["container-background"] == "dark" ? "text-white" : "text-black"} dark:text-white smooth`}>
                      {popupTitle}
                    </h4>
                  </div>

                  <div className="row hard-center cta-preview" style={{ padding: "20px 0" }}>
                    <div
                      className={`cta${cta.id} ${popupClass}`}
                      data-cta-id={cta.id}
                      style={{ position: "relative", display: "block" }}
                    >
                      <VariantTwoTapPopup
                        cta={cta}
                        variant={variant}
                        website={website}
                        user={user}
                        builder={true}
                        device={contextData.device}
                      />
                    </div>
                  </div>
                </div>
              )}
              {sorted_steps.map((step) => {
                return (
                  <StepBuilder
                    key={"StepBuilder" + step.id}
                    className="step"
                    website={website}
                    cta={cta}
                    variant={variant}
                    user={user}
                    step={step}
                    builder={true}
                    editing={contextData.selected_object_editing}
                    device={contextData.device}
                    section_templates={props.section_templates}
                    {...props}
                  />
                );
              })}
            </div>
            <div
              onClick={() => {
                dispatchCustomEvent("newObject", {
                  object_type: "Steps",
                });
              }}
              className="row hard-center vertical-space step-add-container text-blue-600 dark:text-white"
            >
              <span
                className="glyphicon glyphicon-plus-sign"
                aria-hidden="true"
              ></span>{" "}
              Add Funnel Step
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <React.Fragment>
      {props.page == "builder" && (
        <React.Fragment>
          <ZoomableCanvas
            builder={true}
            initialScale={
              cta.cta_type == "page"
                ? window.innerWidth < 1200
                  ? 0.5
                  : window.innerWidth <= 1920
                  ? 0.75
                  : 1
                : window.innerWidth <= 1920
                ? 0.75
                : 1
            }
            initialX={
              cta.cta_type == "page"
                ? window.innerWidth < 1200
                  ? contextData.zoomable_canvas_locked ? 150 : 400
                  : window.innerWidth <= 1920
                  ? contextData.zoomable_canvas_locked ? 150 : 300
                  : contextData.zoomable_canvas_locked ? 150 : 300
                : window.innerWidth <= 1920
                ? contextData.zoomable_canvas_locked ? 150 : 300
                : contextData.zoomable_canvas_locked ? 150 : 200
            }
          >
            {canvasContainer()}
          </ZoomableCanvas>
          <VariantBuilderCanvasSelect
            selection={{
              object_type: contextData.selected_object_type,
              object_id: contextData.selected_object_id,
              column: contextData.selected_object_collapse,
            }}
            revision={contextData}
          />
          <VariantBuilderCanvasSelectPopover />

          <div
            data-canvas-boundary="right"
            className="h-screen fixed right-0 bg-white dark:bg-slate-950 mt-[49px] pt-4 center smooth z-10"
            style={{
              width: "10px",
            }}
          >
            <VariantBuilderCanvasRoundedCorner
              vertical="top"
              horizontal="right"
            />
            <VariantBuilderCanvasRoundedCorner
              vertical="bottom"
              horizontal="right"
            />
          </div>
          <div
            data-canvas-boundary="bottom"
            className="w-screen fixed bottom-0 bg-white dark:bg-slate-950 center smooth z-10"
            style={{
              height: "10px",
            }}
          ></div>
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

export default VariantBuilderCanvas;
